import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Box, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { signOut } from '../lib/auth';
import { NAV_GROUPS, SCREEN_META } from '../nav';
import { useSession } from '../lib/session';
import { useTRPC } from '../lib/trpc';
import classes from './AppShell.module.css';

/**
 * The shell from the mockup: 238px sidebar, sticky blurred header, theme switch and the
 * role read-out. Navigation rows are filtered by the permission matrix the API returns —
 * the client never decides access, it only mirrors the server's answer.
 */
/**
 * The mockup writes large counters short — `sites 5.2k`, `slots 3.1k` — because a
 * five-digit number in a 238px row pushes the label out. Below 1000 it prints the number
 * as it is, so «412» stays «412» rather than becoming «0.4k».
 */
function formatBadge(value: number): string {
  if (value < 1000) return String(value);
  const thousands = value / 1000;
  return `${thousands < 10 ? thousands.toFixed(1) : String(Math.round(thousands))}k`;
}

export function AppShell(): React.JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  /**
   * Badges are chrome: a failure here must not take the shell down, and a slow count must
   * not hold the first paint. The sidebar renders without numbers and fills them in.
   *
   * Degrading to no-badges is deliberately silent **in the browser only** — a broken
   * counter is not worth a red banner over the whole app. It is not silent to us: the
   * procedure runs server-side, so a failure is a 500 in the API log and in Sentry, and
   * `00-screen-inventory` / the badge assertion in `01-auth-rbac` fail in CI. That
   * combination is what makes the silence safe; without it this would be a screen that
   * looks fine while a query throws on every render, which is how the first version of
   * this query shipped a bad enum literal.
   */
  const badges = useQuery({
    ...trpc.session.navBadges.queryOptions(),
    staleTime: 60_000,
    retry: false,
  });

  const logout = useMutation({
    mutationFn: signOut,
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/login' });
    },
  });

  /**
   * A role the matrix does not know, or an account switched off mid-session, gets a full-page
   * notice instead of the shell: `zones` is already all-`none` for both (`session.me`), so the
   * sidebar would render with every row hidden and no explanation for why. `twoFactorSetupRequired`
   * does not reach this component — `authedRoute.beforeLoad` (`router.tsx`) redirects to
   * `/setup-2fa` before the shell ever mounts.
   */
  if (session && (session.role === null || !session.active)) {
    const message =
      session.role === null
        ? 'Роль облікового запису не розпізнана — зверніться до адміністратора'
        : 'Обліковий запис деактивовано';
    return (
      <Box className={classes.root} data-testid="account-notice">
        <Stack align="center" justify="center" gap="md" style={{ minHeight: '100vh', width: '100%' }}>
          <Text data-testid="account-notice-message">{message}</Text>
          <UnstyledButton
            className={classes.logout}
            data-testid="account-notice-signout"
            onClick={() => {
              logout.mutate();
            }}
          >
            Вийти
          </UnstyledButton>
        </Stack>
      </Box>
    );
  }

  const meta = SCREEN_META[pathname] ?? screenMetaForNested(pathname);
  const initials = session
    ? session.name
        .split(' ')
        .map((part) => part.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '··';

  return (
    <Box className={classes.root}>
      <Box component="aside" className={classes.sidebar}>
        <Box className={classes.brand}>
          <Box className={classes.logo}>S</Box>
          <Stack gap={1} className={classes.brandText}>
            <Text className={classes.brandName}>SEO Ops Core</Text>
            <Text className={classes.brandMeta} data-testid="brand-meta">
              внутрішня CRM
            </Text>
          </Stack>
        </Box>

        <Box component="nav" className={classes.nav} data-testid="sidebar-nav">
          {NAV_GROUPS.map((group) => {
            const visible = group.items.filter((item) => session?.zones[item.zone]?.read === true);
            if (visible.length === 0) return null;
            return (
              <Stack key={group.title} gap={2}>
                <Text className={classes.groupTitle}>{group.title}</Text>
                {visible.map((item) => {
                  const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
                  // `null` means the actor may not read that registry — no badge at all.
                  // `0` is a real answer and prints, because an empty registry is a fact
                  // the operator needs on the way to «Створіть перший».
                  const count = item.badge ? badges.data?.[item.badge] : undefined;
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={classes.navItem}
                      data-active={active || undefined}
                      data-testid={`nav-${item.key}`}
                      // Below 1280 the label is hidden and the row is an icon; without this
                      // the compact sidebar is unreadable.
                      title={item.label}
                    >
                      <span className={classes.navIcon}>{item.icon}</span>
                      <span className={classes.navLabel}>{item.label}</span>
                      {typeof count === 'number' ? (
                        <span className={classes.navBadge} data-testid={`nav-badge-${item.key}`}>
                          {formatBadge(count)}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </Stack>
            );
          })}
        </Box>

        <Box className={classes.sidebarFooter}>
          <Box className={classes.switcher}>
            <UnstyledButton
              className={classes.switchButton}
              data-active={colorScheme === 'dark' || undefined}
              onClick={() => {
                setColorScheme('dark');
              }}
              data-testid="theme-dark"
            >
              ТЕМНА
            </UnstyledButton>
            <UnstyledButton
              className={classes.switchButton}
              data-active={colorScheme === 'light' || undefined}
              onClick={() => {
                setColorScheme('light');
              }}
              data-testid="theme-light"
            >
              СВІТЛА
            </UnstyledButton>
          </Box>

          {/*
            The two-button switcher does not fit a 56px sidebar, so the compact width gets
            its own control rather than a squeezed one. Both live in the DOM and CSS picks;
            deciding in JS would need a width listener and would flash on first paint.
          */}
          <UnstyledButton
            className={classes.switchCompact}
            onClick={() => {
              setColorScheme(colorScheme === 'dark' ? 'light' : 'dark');
            }}
            data-testid="theme-toggle-compact"
            title={colorScheme === 'dark' ? 'Світла тема' : 'Темна тема'}
            aria-label={colorScheme === 'dark' ? 'Світла тема' : 'Темна тема'}
          >
            {colorScheme === 'dark' ? '☾' : '☀'}
          </UnstyledButton>

          <Group gap={8} px={4} pt={2} className={classes.userRow}>
            <Box className={classes.avatar} title={session?.name ?? undefined}>
              {initials}
            </Box>
            <Stack gap={0} className={classes.userText} style={{ flex: 1, minWidth: 0 }}>
              <Text className={classes.userName} data-testid="current-user">
                {session?.name ?? '—'}
              </Text>
              <Text className={classes.userRole} data-testid="current-role">
                {session?.roleLabel ?? '—'}
              </Text>
            </Stack>
            <UnstyledButton
              className={classes.logout}
              onClick={() => {
                logout.mutate();
              }}
              data-testid="logout"
              aria-label="Вийти"
            >
              ⏻
            </UnstyledButton>
          </Group>
        </Box>
      </Box>

      <Box component="main" className={classes.main}>
        <Box component="header" className={classes.header}>
          <Stack gap={2}>
            <Text className={classes.screenTitle} data-testid="screen-title">
              {meta.title}
            </Text>
            <Text className={classes.screenCrumb}>{meta.crumb}</Text>
          </Stack>
          <QueueChip />
        </Box>
        <Box className={classes.content}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * «Черга генерації · N» із шапки мокапа.
 *
 * Таблиця `jobs` писалась із першого дня і не читалась нізвідки, тож єдиним місцем, де стан
 * черги був видимий, лишався SSH. Число тут — це `queued + active`: робота, яка ще має
 * статися. Мертві показані окремо й червоним, бо це протилежне за змістом: вони вже не
 * станутьcя ніколи, і поки їх не помітять, сторінки лишаються порожніми.
 */
function QueueChip(): React.JSX.Element | null {
  const trpc = useTRPC();
  const counts = useQuery({
    ...trpc.queue.counts.queryOptions({}),
    staleTime: 15_000,
    retry: false,
  });

  if (counts.data === undefined) return null;
  const pending = counts.data.queued + counts.data.active;
  const { dead } = counts.data;
  if (pending === 0 && dead === 0) return null;

  return (
    <Group gap={6} className={classes.headerRight} data-testid="queue-chip">
      <Text className={classes.screenCrumb}>Черга генерації · {pending}</Text>
      {dead === 0 ? null : (
        <Text className={classes.queueDead} data-testid="queue-chip-dead">
          мертвих: {dead}
        </Text>
      )}
    </Group>
  );
}

/** Nested routes (`/sites/:id`) inherit their section's header rather than showing blank. */
function screenMetaForNested(pathname: string): { title: string; crumb: string } {
  const match = Object.keys(SCREEN_META)
    .filter((key) => key !== '/' && pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
  return match ? (SCREEN_META[match] as { title: string; crumb: string }) : { title: '', crumb: '' };
}
